import { useEffect, useState } from 'react';
import { useStore } from 'react-admin';

import { requireAuth } from '@src/providers/dataProvider/shared';
import type { Partner } from '@src/types';

import { fetchSftpFolderMappingConfigured } from './fetchSftpFolderMappingConfigured';

/**
 * Whether this partner's SFTP folder mapping has actually been set up, used
 * as a proxy for whether the Files portal should be shown/accessible at all.
 * `isLoading` lets callers (e.g. the /files route guard) avoid rendering an
 * "access denied" state before the check has actually resolved.
 */
export const useSftpFoldersConfigured = (): { isConfigured: boolean, isLoading: boolean } => {
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!currentPartner?.code) {
      setIsConfigured(false);
      setIsLoading(false);
      return undefined;
    }

    setIsConfigured(false);
    setIsLoading(true);

    let isMounted = true;
    const partnerCode = currentPartner.code;

    const check = async () => {
      try {
        const token = requireAuth();
        const configured = await fetchSftpFolderMappingConfigured({ token, partnerCode });
        if (isMounted) setIsConfigured(configured);
      } catch {
        if (isMounted) setIsConfigured(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    void check();

    return () => { isMounted = false; };
  }, [currentPartner?.code]);

  return { isConfigured, isLoading };
};
